// Layer 3 (Transformation) — the PM schedule's work lanes (polish.P21, comp A
// finished with variant B's lanes as the first region of the PM schedule tab).
// Pure: no React, no engine mutation, no clock read of its own.
//
// The open schedule on one day axis: a lane of shutdown windows, then one lane
// per open PM in due order, each block at the day shift of its due date and as
// wide as the task's hours. An overdue task draws from its due date to the
// cursor (its lateness), a task due today and a scheduled one draw their hours.
// The BM01 bearing's proposed stop is the case's draft action (D16): it draws
// as a dashed block in its own lane and is never a schedule row. The cursor and
// the bearing's projected 85 °C instant are ruled through every lane.
//
// durable: the PM schedule's lane read over the typed plan (ADR-0012).

import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import type { SessionState } from '@/versions/v1/schema/scenario';
import { addMinutes, diffMinutes, formatDayMonth } from '@/versions/v1/shared/clock.derive';
import { CASES } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import { BM01_BEARING_STOP_HOURS } from '@/versions/v1/shared/bunker-hill-bearing-stop';
import { bm01BearingTrend } from '@/versions/v1/modules/downtime/ValueLost.transform';
import { DOWNTIME_ITEMS } from './downtime.seed';
import type { PmTask } from './pm.seed';
import { pmTaskRows, type PmStatus } from './PmSchedule.transform';
import type { ShutdownWindow } from './strategy.seed';

export type LaneBlockKind = 'overdue' | 'due-today' | 'scheduled' | 'window' | 'draft';

export type LaneBlock = {
  startAt: SeriesTime;
  endAt: SeriesTime;
  kind: LaneBlockKind;
  label: string;
};

export type Lane = {
  id: string;
  label: string;
  /** The windows lane heads the chart and reads as a caps group label. */
  group: boolean;
  blocks: readonly LaneBlock[];
};

export type PmLanesRead = {
  window: { startAt: SeriesTime; endAt: SeriesTime };
  dayTicks: readonly { at: SeriesTime; label: string }[];
  now: SeriesTime;
  /** The bearing's projected limit instant, ruled through every lane. */
  limitAt: SeriesTime | null;
  lanes: readonly Lane[];
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** Day shift opens at 06:00 — where a window with only a date sits. */
const DAY_SHIFT_START = '06:00';

function floorDay(at: SeriesTime): SeriesTime {
  return `${at.slice(0, 10)}T00:00` as SeriesTime;
}

function ceilDay(at: SeriesTime): SeriesTime {
  const floor = floorDay(at);
  return floor === at ? at : addMinutes(floor, 24 * 60);
}

/** `13 Jun` (a shutdown window's start, typed as its label) at the day shift of
 *  that date in the cursor's year. */
export function windowStartAt(label: string, cursor: SeriesTime): SeriesTime | null {
  const match = /^(\d{2}) ([A-Z][a-z]{2})$/.exec(label.trim());
  if (!match) return null;
  const month = MONTHS.indexOf(match[2]!);
  if (month < 0) return null;
  return `${cursor.slice(0, 4)}-${String(month + 1).padStart(2, '0')}-${match[1]}T${DAY_SHIFT_START}` as SeriesTime;
}

const BLOCK_KIND: Record<Exclude<PmStatus, 'complete'>, LaneBlockKind> = {
  overdue: 'overdue',
  'due-today': 'due-today',
  scheduled: 'scheduled',
};

function hoursLabel(hours: number): string {
  return `${Number.isInteger(hours) ? hours : hours.toFixed(1)} h`;
}

export function pmLanes(input: {
  tasks: readonly PmTask[];
  windows: readonly ShutdownWindow[];
  state: SessionState;
}): PmLanesRead {
  const { tasks, windows, state } = input;
  const cursor = state.cursor;
  const cursorFloor = floorDay(cursor);
  const open = pmTaskRows(tasks, cursor);

  const taskLanes: { lane: Lane; at: SeriesTime }[] = open.map((row) => {
    const kind = BLOCK_KIND[row.status as Exclude<PmStatus, 'complete'>];
    const days = diffMinutes(cursorFloor, floorDay(row.dueDate)) / (24 * 60);
    const block: LaneBlock =
      kind === 'overdue'
        ? { startAt: row.dueDate, endAt: cursor, kind, label: `${row.task} · ${days} days late` }
        : { startAt: row.dueDate, endAt: addMinutes(row.dueDate, row.hours * 60), kind, label: `${row.task} · ${hoursLabel(row.hours)}` };
    return { lane: { id: row.id, label: row.asset, group: false, blocks: [block] }, at: row.dueDate };
  });

  // The draft stop: the case with a stop plan, drawn in its item's lane.
  const draftCase = CASES.find((row) => row.assignedTo === 'maintenance' && row.stopPlan !== undefined);
  const draftAt = draftCase?.stopPlan?.window.at as SeriesTime | undefined;
  if (draftCase && draftAt) {
    const hours = BM01_BEARING_STOP_HOURS.plannedInspection;
    // The work order's asset leads with the item id (`BM01 · trunnion bearing`).
    const assetId = draftCase.stopPlan?.workOrder?.asset.split(' ')[0] ?? '';
    const itemLabel = DOWNTIME_ITEMS.find((row) => row.id === assetId)?.label ?? draftCase.stopPlan?.workOrder?.asset ?? draftCase.title;
    taskLanes.push({
      lane: {
        id: `draft-${draftCase.id}`,
        label: itemLabel,
        group: false,
        blocks: [{ startAt: draftAt, endAt: addMinutes(draftAt, hours * 60), kind: 'draft', label: `Proposed stop · ${hoursLabel(hours)}` }],
      },
      at: draftAt,
    });
  }
  // Due order; on a tie the draft follows the scheduled work.
  taskLanes.sort((a, b) => (a.at < b.at ? -1 : a.at > b.at ? 1 : a.lane.id.startsWith('draft-') ? 1 : b.lane.id.startsWith('draft-') ? -1 : 0));

  const windowBlocks: LaneBlock[] = windows.flatMap((row) => {
    const at = windowStartAt(row.plannedStart, cursor);
    return at
      ? [{ startAt: at, endAt: addMinutes(at, row.durationHours * 60), kind: 'window' as const, label: `${row.area} · ${hoursLabel(row.durationHours)}` }]
      : [];
  });
  windowBlocks.sort((a, b) => (a.startAt < b.startAt ? -1 : a.startAt > b.startAt ? 1 : 0));

  const lanes: Lane[] = [
    { id: 'windows', label: 'Shutdown windows', group: true, blocks: windowBlocks },
    ...taskLanes.map(({ lane }) => lane),
  ];

  const trend = bm01BearingTrend(state);
  const limitAt = draftCase && trend.state === 'fitted' ? trend.limitAt : null;
  const allBlocks = lanes.flatMap((lane) => lane.blocks);
  const starts = [cursor, ...allBlocks.map((block) => block.startAt)];
  const ends = [cursor, ...allBlocks.map((block) => block.endAt), ...(limitAt ? [limitAt] : [])];
  const startAt = floorDay(starts.reduce((low, at) => (at < low ? at : low)));
  const endAt = ceilDay(ends.reduce((high, at) => (at > high ? at : high)));
  const dayTicks: { at: SeriesTime; label: string }[] = [];
  for (let at = startAt; at < endAt; at = addMinutes(at, 24 * 60)) {
    dayTicks.push({ at, label: formatDayMonth(at) });
  }

  return { window: { startAt, endAt }, dayTicks, now: cursor, limitAt, lanes };
}

// ---------------------------------------------------------------------------
// Lane label placement (polish.P21 r1). The render maps times to pixels with
// `laneAxis` and asks `laneLabelPlacements` which side of its block each label
// sits on — pure geometry over the read's own window, so no label lands in the
// Now rule's clear air.
// ---------------------------------------------------------------------------

/** The lane chart's own geometry: the label column the lane names occupy and
 *  the chart's right padding. The render lays the svg out at the same numbers. */
const LANE_LABEL_W = 200;
export const LANE_PAD_R = 10;
/** About how wide one character of an 11px label runs, for edge flips only. */
const CHAR_W = 6;
/** Clear air between a label and the block it belongs to (px). */
const LABEL_GAP = 6;
/** The clear air a label's left edge keeps from the Now rule (px). */
export const NOW_CLEAR = 6;

/** Times to x over the read's own window, at the chart's measured width. */
export function laneAxis(read: PmLanesRead, width: number): (at: SeriesTime) => number {
  const span = diffMinutes(read.window.endAt, read.window.startAt);
  const inner = width - LANE_LABEL_W - LANE_PAD_R;
  return (at) => LANE_LABEL_W + (span <= 0 ? 0 : inner * (diffMinutes(at, read.window.startAt) / span));
}

export type LaneLabelPlacement = {
  side: 'left' | 'right';
  /** The text's anchor x: `right` starts at it, `left` ends at it. */
  x: number;
};

/** Which side of its block each lane label sits on, right to left along the
 *  lane. A label sits right of its block when that neither runs past what the
 *  next block (or its label) already occupies nor starts inside the Now rule's
 *  clear air; a block that ends inside that air offsets its label just past the
 *  rule (the comp's own read), and only a label that cannot fit there moves to
 *  the far side of its block. The draft always labels to its left — never over
 *  its own future; an overdue block ends at the cursor, so its label reads from
 *  the rule out. */
export function laneLabelPlacements(input: {
  lanes: readonly Lane[];
  width: number;
  axis: (at: SeriesTime) => number;
  now: SeriesTime;
}): LaneLabelPlacement[][] {
  const { lanes, width, axis, now } = input;
  const nowX = axis(now);
  return lanes.map((lane) => {
    const placements: LaneLabelPlacement[] = lane.blocks.map(() => ({ side: 'right', x: 0 }));
    let occupiedFrom = width - LANE_PAD_R;
    for (let i = lane.blocks.length - 1; i >= 0; i -= 1) {
      const block = lane.blocks[i]!;
      const bx = axis(block.startAt);
      const bw = Math.max(3, axis(block.endAt) - bx);
      const labelW = block.label.length * CHAR_W;
      const rightX = bx + bw + LABEL_GAP;
      const limit = occupiedFrom - LABEL_GAP;
      // Right of the block, when it fits and its left edge is outside the
      // rule's clear air (fully left of it, or clear on the right).
      const natural = rightX + labelW <= limit && (rightX >= nowX + NOW_CLEAR || rightX <= nowX - NOW_CLEAR);
      // The block ends inside the rule's clear air: offset the label to the
      // rule's far side, where it fits there.
      const offsetX = nowX + NOW_CLEAR;
      const offset = !natural && rightX < offsetX && offsetX + labelW <= limit;
      // The draft never runs over its own future: it always labels to its left.
      const left = block.kind === 'draft' || (!natural && !offset);
      placements[i] = left ? { side: 'left', x: bx - LABEL_GAP } : { side: 'right', x: offset ? offsetX : rightX };
      occupiedFrom = left ? bx - LABEL_GAP - labelW : placements[i]!.x;
    }
    return placements;
  });
}
